/**
 * The decision behind the "Manage changed data" pre-flight, as a pure function.
 *
 * Kept out of the hook because these are the parts that are easy to get wrong and impossible to see in a
 * clickthrough: a figure nobody cites must not stop the user with a dialog, and a pre-flight that failed to
 * load must not stop them either.
 */

/** Ask the user what to do about the affected answers before writing. */
export const IMPACT_GATE_ASK = "ask";
/** Write straight away — nothing depends on these figures. */
export const IMPACT_GATE_PROCEED = "proceed";

/**
 * Whether the pre-flight is worth asking for at all.
 *
 * Nulls and duplicates are dropped: a row written before datapoint versioning can carry no uid, and asking
 * about a null finds nothing while making the request look meaningful.
 *
 * @param {Array<string|null|undefined>} datapointUids the figures about to change
 * @returns {string[]} the uids worth asking about — empty means skip the check entirely
 */
export function uidsWorthChecking(datapointUids) {
  return [...new Set((datapointUids ?? []).filter((uid) => !!uid && String(uid).trim() !== ""))];
}

/**
 * What to do once the impact has come back — or failed to.
 *
 * An impact with no requirements means no report answer rests on these figures, so there is no choice to offer
 * and the write proceeds silently.
 *
 * <b>A missing or malformed response takes that same branch, on purpose: this gate fails OPEN.</b> The write
 * was allowed before this feature existed, so a transient failure must not stop someone editing their own
 * data. The cost is that the choice is lost for that write, which is why the caller says so out loud rather
 * than proceeding quietly. Handling the two cases with one rule is what stops a later refactor making a failed
 * pre-flight block the write.
 *
 * @param {{requirements?: Array<object>}|null|undefined} impact the server's answer, or null if it failed
 */
export function decideOnImpact(impact) {
  return (impact?.requirements?.length ?? 0) > 0 ? IMPACT_GATE_ASK : IMPACT_GATE_PROCEED;
}
